import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { StatefulService } from '@app/service/stateful.service';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { PdfEmbedComponent } from '@cudirect/shared-ui-library/pdf-embed';
import { Communication } from '@shared/model/communication.model';
import {
  CrossSellOfferDetails,
  CrossSellOfferResp,
  CrossSellRequireModule,
  CrossSellView,
  NewCreditOffersEntity,
  OffersEntity
} from '@shared/model/cross-sell.model';
import { CrossSellService } from '@shared/service/cross-sell.service';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { LenderService } from '@shared/service/lender/lender.service';
import { LoanService } from '@shared/service/loan.service';
import { EcpSharedModule } from '@workflow/ecp-shared/shared.module';
import { CommonService } from '@workflow/legacy';
import { PostappService } from 'src/postapp/postapp.service';
import { CrossSellCardComponent } from './cross-sell-card/cross-sell-card.component';
import { CrossSellOneClickPanelComponent } from './cross-sell-one-click-panel/cross-sell-one-click-panel.component';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';

@Component({
  selector: 'dx-cross-sell',
  templateUrl: './cross-sell.component.html',
  styleUrls: ['./cross-sell.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CrossSellCardComponent,
    SharedUiLibraryModule,
    EcpSharedModule,
    PdfEmbedComponent,
    CrossSellOneClickPanelComponent
  ],
  providers: [CurrencyPipe]
})
export class CrossSellComponent implements OnInit, OnDestroy {
  private logger = inject(SawDust);

  // Using inject pattern for all dependencies
  public readonly lenderService = inject(LenderService);
  public readonly crossSellService = inject(CrossSellService);
  private readonly cp = inject(CurrencyPipe);
  private readonly commonService = inject(CommonService);
  public readonly userService = inject(UserService);
  private readonly loanServices = inject(LoanService);
  private readonly statefulService = inject(StatefulService);
  private readonly googleTrackingService = inject(GoogleTrackingService);
  private readonly postappService = inject(PostappService);

  /** Polling interval for cross-sell product retrieval */
  private interval: number;
  /** Return loan Number */
  @Input() public appNumber: string;

  /** Specify where to render this component */
  @Input() public displayLocation: string = CrossSellRequireModule.OAOPostApp;

  /** Storage ID */
  public APPLICATION_ID_PATH = 'api:oaoMemberInformation/accountsAndServices/applicationId';

  /** Flag Indicate LDR response API or Mockup */
  // public IS_LDR_API_ENABLED = getMatIconFailedToSanitizeLiteralError;

  /** Indicate showing loader of server request */
  public showLoader = true;

  /** Getting cross sell opertunities for card display */
  public crossSellProducts: { refinanceOffers: CrossSellView[]; creditOffers: CrossSellView[] };

  /** Submitting to L360 cross sell opertunities */
  public objCrossSellOfferDetails: CrossSellOfferDetails;

  /** Icon for slideout panel */
  public icon: string;

  public activeCrossSellOffer: { bundle: any; crossSellOffer: CrossSellView; token: string };

  /** call back msg change */
  public isDataLoaded = false;

  /** call back msg change */
  public btnCallbackSuccess = false;

  // Slide In panel toggle
  public openSlideInPanel = false;

  // CrossSell modal terms and conditions text
  public termsAndConditionsText: string;

  // Stores Cross-Sell Modal Title
  public docTitle = '';

  // stores pdf data in blob format
  public pdfBlob: Blob;

  public pdfURL: string;

  // Toggles content in crossSell TermsAndConditions modal
  public isContentLoaded = false;
  public stipulationList: string[] = [];
  public showStipulationsOnSlideIn = false;
  /** Polling Count */
  public pollingCount: number;

  /**
   * Load to do items details on Init.
   */
  public ngOnInit(): void {
    this.pollingCount = 0;
    this.startCrossSellPolling();
  }

  /**
   * Function to get loan details.
   */
  private getCrossSellProducts(): void {
    const applicationId = this.statefulService.wrap(this.APPLICATION_ID_PATH).valueString;
    this.appNumber = this.appNumber ? this.appNumber : applicationId;

    if (this.appNumber) {
      this.crossSellService.GetCrossSellConfiguration(this.appNumber).subscribe(
        (response) => {
          if (response?.isSuccess) {
            if (response.data) {
              this.crossSellProducts = {
                refinanceOffers: response.data
                  .filter((x) => x.crossSellType.toLocaleLowerCase() === 'refinanceoffer')
                  .filter((n) => this.crossSellFilter(n.offerDetails)),
                creditOffers: response.data
                  .filter((x) => x.crossSellType.toLocaleLowerCase() === 'creditoffer')
                  .filter((n) => this.crossSellFilter(n.offerDetails))
              };
              const crossSellProductsLength =
                this?.crossSellProducts?.refinanceOffers?.length + this?.crossSellProducts?.creditOffers?.length;
              this.isDataLoaded = crossSellProductsLength > 0 ? true : false;
            }
            this.showLoader = false;
          }
        },
        () => {
          this.showLoader = false;
          this.isDataLoaded = false;
        }
      );
    }
  }

  /**
   * Initiates a polling process to retrieve cross-sell products.
   *
   * This private method starts a polling process that repeatedly calls `getCrossSellProducts` at a specified interval.
   * The polling occurs until one of the following conditions is met:
   * 1. New credit offers or refinance opportunities are found within `crossSellProducts`.
   * 2. The maximum number of polling attempts, defined by `POLL_COUNT`, is reached.
   */
  private startCrossSellPolling(): void {
    // Config for polling
    const POLL_COUNT = 5;
    const POLL_INTERVAL_MS = 5000;

    this.interval = setInterval(() => {
      this.pollingCount += 1;
      this.getCrossSellProducts();
      if (
        (this.crossSellProducts &&
          (this.crossSellProducts?.creditOffers?.length > 0 || this.crossSellProducts?.refinanceOffers?.length > 0)) ||
        this.pollingCount >= POLL_COUNT
      ) {
        clearInterval(this.interval);
      }
    }, POLL_INTERVAL_MS) as unknown as number;
  }

  /**
   * Function sync after loan cta click call
   */
  public onLoanClick(offerRes: CrossSellOfferResp): void {
    // Cross sell CTA selected
    this.googleTrackingService.publishEvent('arcdx.postapp.cross-sell-cta', {});

    const { offerAction, bundle, offer, fullCrossSellData } = offerRes;
    if (offerAction.toLowerCase() === 'oneclick') {
      this.postappService.stopPolling();
      this.postappService.stopStatusPolling();
      this.openCrossSellSlideInPanel({
        oneClickOpen: true,
        bundle,
        crossSellOffer: fullCrossSellData,
        token: offerRes?.token,
        stipulationList: fullCrossSellData ? this.crossSellService.stipulationHelper(fullCrossSellData?.offerDetails.stipulation) : []
      });
      return;
    } else if (offerAction.toLowerCase() === 'deeplink' && offerRes?.token) {
      this.postappService.stopPolling();
      this.postappService.stopStatusPolling();
      this.crossSellService.navigateToDeepLinkOffer(offerRes.token, fullCrossSellData.groupId, fullCrossSellData.productId)
      .subscribe({error: (err) => this.commonService.errorHandling(err)});
      return;
    }

    this.updateCommunicationNotes(offer.offers[0]);
  }

  // Handle close event from one-click panel  
  // So we can reload postapp data if needed
  public crossSellComplete(event: { refreshed: boolean }): void {
    this.postappService.refreshOverview();
        // Reload do polling for application task and status check for submitted loans
        // this.postappService.startPolling();
    this.openSlideInPanel = false;

    this.resetPanel();  
    // Esither check for new crosssell for submitted app or just reset
    this.crossSellProducts=undefined;
    this.isDataLoaded=true;
    setTimeout(() => {
      this.getCrossSellProducts();
    }, 0);
    // if (event.refreshed) {
    //   this.crossSellProducts=undefined;
    //   this.getCrossSellProducts();
    // } else {
    //   this.resetPanel();
    // }
  }

  private updateCommunicationNotes(Offers: OffersEntity): void {
    const reqBody: Communication = {
      ApplicationId: this.appNumber,
      IsHighPriority: true,
      IsSticky: true,
      LenderCode: this.lenderService.lenderDataInfo.consumerLoanCode,
      Message: this.getMessage(Offers)
    } as Communication;
    this.crossSellService.SaveCommunicationMessage(reqBody).subscribe(
      (response) => {
        if (response?.isSuccess) {
          // this.btnAcceptedOfferEmit.emit(objAcceptedOffer);
          this.btnCallbackSuccess = true;
          this.showLoader = false;
        }
      },
      (err) => {
        this.logger.errorOnly(err);
        this.showLoader = false;
      }
    );
  }

  private crossSellFilter(cs: NewCreditOffersEntity): boolean {
    return (
      cs.crossSellStatusDescription &&
      (cs.crossSellStatusDescription.toLocaleLowerCase() === 'interested' || cs.crossSellStatusDescription.toLocaleLowerCase() === 'new') &&
      cs.decisionAmount > 0
    );
  }
  private getMessage(offer: OffersEntity): string {
    const baseMessage = `Online applicant expressed interest in cross sell: ${offer.description}`;
    const amountMessage = this.cp.transform(offer.amount, 'USD', 'symbol', '1.2-2');
    const rateMessage = offer.rate > 0 ? `at ${offer.rate}%` : '';
    const termMessage = offer.term > 0 ? `for ${offer.term} months` : '';

    return `${baseMessage} ${amountMessage} ${rateMessage} ${termMessage}`.trim();
  }

  /**
   * Reset all slide in panel data
   */
  public resetPanel(): void {
    this.activeCrossSellOffer = null;
    this.pdfBlob = null;
    this.pdfURL = null;
    this.termsAndConditionsText = null;
    this.isContentLoaded = false;
    this.stipulationList = [];
    this.showStipulationsOnSlideIn = false;
  }

  // Open up the panel for one click cross sell, else open up to view terms and conditions?
  public openCrossSellSlideInPanel({ oneClickOpen, bundle, crossSellOffer, token, stipulationList }): void {
    // Clear everything before reopening panel so old data isn't there.
    this.resetPanel();
    if (oneClickOpen) {
      this.activeCrossSellOffer = { bundle: bundle, crossSellOffer: crossSellOffer, token: token };
    }
    this.docTitle = oneClickOpen ? `${crossSellOffer?.displayName} Application` : crossSellOffer?.offerDetails.documentLinkDisplayText;
    this.termsAndConditionsText = crossSellOffer?.offerDetails?.termsAndConditions
      ? this.crossSellService.parseCustomVariable(crossSellOffer?.offerDetails?.termsAndConditions, crossSellOffer)
      : null;
    if (crossSellOffer?.offerDetails.docRepID) this.getCrossSellDocument(crossSellOffer.offerDetails.docRepID);

    this.showStipulationsOnSlideIn = this.crossSellService.displayStipulationOnSlideInPanel(stipulationList);
    this.isContentLoaded = true;
    this.stipulationList = stipulationList;
    this.openSlideInPanel = true;
  }

  private getCrossSellDocument(docRepID: string): void {
    this.loanServices
      .getDocumentByDocRepID(docRepID)
      .then((value) => {
        this.pdfURL = value;
      })
      .catch((err) => {
        this.commonService.errorHandling(err);
      });
  }

  public ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}
